import * as React from 'react';
import { ArrowRightIcon } from '@mentimeter/ragnar-visuals';
import type { Placement } from '@mentimeter/presentation-state';
import type { ClickableT } from '@mentimeter/ragnar-ui/clickable';
import { useTranslations } from 'next-intl';
import { Hotkey } from '../../misc/hotkeys/Hotkey';
import { ToolbarButton } from '../ToolbarItem';

interface NextProps extends ClickableT {
  next: (placement: Placement) => void;
  nextStepLabel: string | undefined;
  hasNext: boolean;
  disableHotkeys?: boolean;
  hideTooltip?: boolean;
}

const nextStepToTranslation = (
  nextStep: string | undefined,
  translate: ReturnType<typeof useTranslations<'presenting.legacy'>>,
) => {
  switch (nextStep) {
    case 'view-answers':
      return translate('toolbar.view_answers');
    case 'start-voting':
      return translate('toolbar.start_voting');
    case 'view-results':
      return translate('toolbar.view_results');
    case 'view-correct-answer':
      return translate('toolbar.view_correct_answer');
    case 'next-slide':
      return translate('toolbar.next_slide');
    case 'end-presentation':
      return translate('toolbar.end_presentation');
    case 'start-quiz':
      return translate('toolbar.start_quiz');
    case 'leaderboard':
      return translate('toolbar.leaderboard');
    default:
      return undefined;
  }
};

export const Next = ({
  next,
  nextStepLabel,
  hasNext,
  disableHotkeys = false,
  hideTooltip = false,
  ...rest
}: NextProps) => {
  const translate = useTranslations('presenting.legacy');
  const translatedNextStep = nextStepToTranslation(nextStepLabel, translate);

  return (
    <>
      <ToolbarButton
        {...rest}
        ariaLabel={translatedNextStep || translate('toolbar.next_slide')}
        id={nextStepLabel || 'next-slide'}
        data-testid="toolbar-next"
        icon={ArrowRightIcon}
        text={translatedNextStep}
        disabled={!hasNext}
        hideTooltip={hideTooltip}
        label={translatedNextStep || translate('toolbar.next_slide')}
        onClick={() => next('Navigation button')}
      />
      {!disableHotkeys && hasNext && (
        <>
          <Hotkey
            preventDefault
            keyCode="pagedown"
            action={() => next('Hotkey pagedown')}
          />
          <Hotkey
            preventDefault
            keyCode="arrowright"
            action={() => {
              next('Hotkey arrowright');
            }}
          />
        </>
      )}
    </>
  );
};
